import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  ColumnHeader,
  Button,
  Notice,
  Icon
} from '@deque/cauldron-react';
import type { v2 } from '@deque/billing-service-client';
import Form from './Form';
import { useLoadedConfiguration } from '../../../common/contexts/Configuration';
import PageTitle from '../../../common/components/PageTitle';
import ScrimmedLoader from '../../../common/components/ScrimmedLoader';
import styles from './AxeAssistantTeamsConfiguration.css';
import { useAuthContext } from '../../../common/contexts/auth';
import {
  addTenantId,
  deleteTenantIdById,
  getTeamsConnections,
  updateTenantIdById,
  getTeamsConnectionByTenantId
} from '../../../common/utils/integrations-client/axe-assistant-teams/connections';
import { validateTenantId } from '../../../common/utils/integrations-client/axe-assistant-teams/validateTenantId';
import { useEnterprises } from '../../../common/contexts/enterprises';

interface TenantId {
  id: string;
  value: string;
}

// Separate interface for editing state
interface EditingState {
  id: string | null;
  value: string;
  error: string | null;
}

interface AxeAssistantTeamsForm {
  newTenantId: string;
}

interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

const AxeAssistantTeamsConfiguration: React.FC<{
  product: v2.Product;
}> = ({ product }) => {
  const { t } = useTranslation();
  const { saving, isEnterpriseAdmin, isEnterpriseMember } =
    useLoadedConfiguration();

  // Auth context
  const { user } = useAuthContext();
  const { activeEnterprise } = useEnterprises();

  // Tenant IDs state
  const [tenantIds, setTenantIds] = useState<TenantId[]>([]);
  const [originalTenantIds, setOriginalTenantIds] = useState<TenantId[]>([]);

  // Status and error states
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tenantIdError, setTenantIdError] = useState<string | null>(null);
  const [validatingTenantId, setValidatingTenantId] = useState(false);
  const [showLimitToast, setShowLimitToast] = useState(false);

  // Form state for tracking changes to tenant IDs
  const [formHasChanges, setFormHasChanges] = useState(false);

  // Editing state
  const [editingTenant, setEditingTenant] = useState<EditingState>({
    id: null,
    value: '',
    error: null
  });

  // Form management
  const { control, setValue, watch, reset } = useForm<AxeAssistantTeamsForm>();
  const newTenantId = watch('newTenantId', '');

  // Permissions
  const isReadOnly = isEnterpriseMember && !isEnterpriseAdmin;
  const disabled = isReadOnly || saving || isSaving;

  // Check if tenant IDs have changed from original
  const checkForChanges = () => {
    if (tenantIds.length !== originalTenantIds.length) {
      return true;
    }

    // Compare sets of values instead of maps with IDs
    const originalSet = new Set(originalTenantIds.map(tenant => tenant.value));
    const currentSet = new Set(tenantIds.map(tenant => tenant.value));

    // Check if sets have different sizes
    if (originalSet.size !== currentSet.size) {
      return true;
    }

    // Check if any value in current set is not in original set
    for (const value of Array.from(currentSet)) {
      if (!originalSet.has(value)) {
        return true;
      }
    }

    return false;
  };

  // Update form dirty state when tenant IDs change
  useEffect(() => {
    setFormHasChanges(checkForChanges());
  }, [tenantIds, originalTenantIds]);

  // Load tenant IDs from backend
  useEffect(() => {
    if (activeEnterprise?.id && user?.token) {
      setIsFetching(true);
      getTeamsConnections({
        enterpriseId: activeEnterprise.id,
        token: user.token
      })
        .then(connections => {
          const existingTenantIds = connections.map(connection => ({
            id: connection.id,
            value: connection.instance_id
          }));
          setTenantIds(existingTenantIds);
          setOriginalTenantIds([...existingTenantIds]);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Failed to load tenant IDs', err);
          setSaveError(t('Failed to load tenant IDs. Please try again.'));
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  }, [activeEnterprise?.id, user?.token]);

  // Clear validation error when tenant ID input changes
  useEffect(() => {
    if (tenantIdError) {
      setTenantIdError(null);
    }
  }, [newTenantId]);

  // Validate tenant ID (reusable function)
  const validateTenantIdValue = async (
    tenantIdValue: string,
    excludeId?: string
  ): Promise<ValidationResult> => {
    if (!tenantIdValue.trim()) {
      return { isValid: false, error: t('Tenant ID cannot be empty') };
    }

    if (!activeEnterprise?.id || !user?.token) {
      return {
        isValid: false,
        error: t('Authentication error. Please try again.')
      };
    }

    // Check if already at maximum limit (1 tenant ID)
    if (tenantIds.length >= 1 && !excludeId) {
      return {
        isValid: false,
        error: t(
          'Only one tenant ID is allowed. Please remove the existing one before adding a new one.'
        )
      };
    }

    // Check for duplicates in current list (excluding the ID being edited if provided)
    const isDuplicate = tenantIds.some(
      tenant =>
        tenant.value === tenantIdValue.trim() &&
        (excludeId === undefined || tenant.id !== excludeId)
    );
    if (isDuplicate) {
      return { isValid: false, error: t('This tenant ID already exists') };
    }

    try {
      // Validate with Microsoft to ensure it's a valid tenant ID
      const validationResult = await validateTenantId(tenantIdValue.trim());
      if (!validationResult) {
        return {
          isValid: false,
          error: t('Invalid Microsoft tenant ID. Please check and try again.')
        };
      }

      // Check if it's already used by another enterprise
      const existingConnection = await getTeamsConnectionByTenantId({
        enterpriseId: activeEnterprise.id,
        token: user.token,
        tenantId: tenantIdValue.trim()
      });

      // If the connection exists but belongs to a different enterprise, that's an error
      if (
        existingConnection &&
        existingConnection.enterprise_id !== activeEnterprise.id
      ) {
        return {
          isValid: false,
          error: t('This tenant ID is already in use by another organization')
        };
      }

      // All validations passed
      return { isValid: true, error: null };
    } catch (err) {
      return {
        isValid: false,
        error: t('Failed to validate tenant ID. Please try again.')
      };
    }
  };

  // Generate a temporary ID for new tenant IDs
  const generateId = () =>
    `tenant-${Math.random().toString(36).substring(2, 9)}`;

  // Add new tenant ID
  const validateAndAddTenantId = async () => {
    if (!newTenantId.trim()) {
      return;
    }

    setValidatingTenantId(true);
    try {
      const validation = await validateTenantIdValue(newTenantId);
      if (!validation.isValid) {
        setTenantIdError(validation.error);
        if (tenantIds.length >= 1) {
          setShowLimitToast(true);
        }
        return;
      }

      // If we get here, the tenant ID is valid and can be added
      setTenantIds([
        ...tenantIds,
        { id: generateId(), value: newTenantId.trim() }
      ]);
      setValue('newTenantId', '');
    } finally {
      setValidatingTenantId(false);
    }
  };

  // Remove tenant ID
  const removeTenantIdFromState = (idToRemove: string) => {
    setTenantIds(tenantIds.filter(tenant => tenant.id !== idToRemove));
  };

  // Start editing a tenant ID
  const startEditing = (id: string) => {
    const tenant = tenantIds.find(tt => tt.id === id);
    if (tenant) {
      setEditingTenant({
        id: tenant.id,
        value: tenant.value,
        error: null
      });
    }
  };

  // Handle editing input change
  const handleEditChange = (value: string) => {
    setEditingTenant(prev => ({
      ...prev,
      value,
      error: null
    }));
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingTenant({
      id: null,
      value: '',
      error: null
    });
  };

  // Save edited tenant ID
  const saveEditedTenantId = async () => {
    if (!editingTenant.id || !editingTenant.value.trim()) {
      cancelEditing();
      return;
    }

    // Validate the edited tenant ID
    const validation = await validateTenantIdValue(
      editingTenant.value,
      editingTenant.id
    );

    if (!validation.isValid) {
      // Show error but keep editing
      setEditingTenant(prev => ({
        ...prev,
        error: validation.error
      }));
      return;
    }

    // If validation passed, update the tenant ID in the main list
    setTenantIds(
      tenantIds.map(tenant =>
        tenant.id === editingTenant.id
          ? { ...tenant, value: editingTenant.value.trim() }
          : tenant
      )
    );

    // Exit editing mode
    cancelEditing();
  };

  // Handle save
  const onSave = async () => {
    if (!activeEnterprise?.id || !user?.token || !user?.id) {
      setSaveError(t('Authentication error. Please try again.'));
      return;
    }

    const enterpriseId = activeEnterprise.id;
    const token = user.token;
    const userId = user.id;

    // Reset status
    setSaveError(null);
    setIsSaving(true);

    const originalIdMap = new Map(
      originalTenantIds.map(tenant => [tenant.id, tenant.value])
    );
    const currentIdMap = new Map(
      tenantIds.map(tenant => [tenant.id, tenant.value])
    );

    const promises: Promise<unknown>[] = [];

    // Identify Deletes, Updates, and Adds
    originalTenantIds.forEach(originalTenant => {
      if (!currentIdMap.has(originalTenant.id)) {
        promises.push(
          deleteTenantIdById({
            enterpriseId,
            token,
            connectionId: originalTenant.id
          })
        );
      } else {
        const currentValue = currentIdMap.get(originalTenant.id);
        if (currentValue !== originalTenant.value && currentValue) {
          promises.push(
            updateTenantIdById({
              enterpriseId,
              token,
              connectionId: originalTenant.id,
              tenantId: currentValue,
              userId
            })
          );
        }
      }
    });

    tenantIds.forEach(currentTenant => {
      if (!originalIdMap.has(currentTenant.id)) {
        // ADD: Exists in current but not original
        // Only add if it has a value (generated IDs are for UI only until saved)
        if (currentTenant.value) {
          promises.push(
            addTenantId({
              enterpriseId,
              token,
              tenantId: currentTenant.value,
              userId
            })
          );
        }
      }
    });

    if (promises.length === 0) {
      setIsSaving(false);
      return;
    }

    try {
      const results = await Promise.allSettled(promises);
      const failedOperations = results.filter(
        result => result.status === 'rejected'
      ) as PromiseRejectedResult[];

      if (failedOperations.length > 0) {
        const errorMessages = failedOperations
          .map(result => result.reason?.message || 'Unknown error')
          .join(', ');
        setSaveError(
          t('Failed to save some tenant IDs: {{errors}}', {
            errors: errorMessages
          })
        );
      } else {
        // All succeeded - refresh connections from server
        const connections = await getTeamsConnections({ enterpriseId, token });
        const updatedTenantIds = connections.map(connection => ({
          id: connection.id,
          value: connection.instance_id
        }));
        setTenantIds(updatedTenantIds);
        setOriginalTenantIds(updatedTenantIds); // Keep references identical
        reset(); // Reset form fields
      }
    } catch (err) {
      setSaveError(t('An unexpected error occurred. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes
  const onDiscard = () => {
    // Restore tenant IDs to their original state
    setTenantIds([...originalTenantIds]);
    setValue('newTenantId', '');
    cancelEditing();
    reset();
    setSaveError(null);
    setTenantIdError(null);
  };

  return (
    <>
      <PageTitle
        title={t('Configuration - {{productName}}', {
          productName: product.name
        })}
      />

      {saveError && (
        <Notice type="info" title={t('Error')}>
          {saveError}
          <Button
            onClick={() => setSaveError(null)}
            className={styles.errorDismissButton}
          >
            {t('Dismiss')}
          </Button>
        </Notice>
      )}

      <Form
        header={product.name}
        isDirty={formHasChanges}
        isReadOnly={isReadOnly}
        onSave={onSave}
        onDiscard={onDiscard}
        data-testid="teams-config-form"
      >
        <form>
          <ColumnHeader>
            <h2>Microsoft Teams Configuration</h2>
          </ColumnHeader>
          <p>
            {t(
              'Add Microsoft Teams tenant IDs to associate with your subscription. Users from these tenants will be able to use the axe Assistant Teams app. Only one tenant ID is allowed for Microsoft Teams integration.'
            )}
          </p>

          {isFetching ? (
            <div className={styles.loaderContainer}>
              <ScrimmedLoader label={t('Loading tenant IDs...')} />
            </div>
          ) : tenantIds.length === 0 ? (
            <div className={styles.teamsConfigContainer}>
              <div className={styles.inputGroupRow}>
                <div className={styles.inputFieldContainer}>
                  <Controller
                    control={control}
                    name="newTenantId"
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        {...field}
                        fieldRef={ref}
                        id="new-tenant-id"
                        aria-label={t('Tenant ID')}
                        label={t('Tenant ID')}
                        disabled={
                          disabled || validatingTenantId || showLimitToast
                        }
                        error={tenantIdError}
                      />
                    )}
                  />
                </div>
                <Button
                  onClick={validateAndAddTenantId}
                  disabled={
                    disabled || validatingTenantId || !newTenantId.trim()
                  }
                  className={styles.addButton}
                  data-testid="add-tenant-id-button"
                >
                  {validatingTenantId ? t('Validating...') : t('Add')}
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.teamsConfigContainer}>
              <div className="Field">
                <label className="Field__label" htmlFor="tenant-id-display">
                  {t('Tenant ID')}
                </label>
                <div className={styles.tenantIdRow}>
                  <div className={styles.tenantIdValue} id="tenant-id-display">
                    {tenantIds[0].value}
                  </div>
                  <div className={styles.buttonGroup}>
                    <Button
                      onClick={() => startEditing(tenantIds[0].id)}
                      disabled={disabled || editingTenant.id !== null}
                      variant="secondary"
                      data-testid="edit-tenant-id-button"
                      aria-label={t('Edit tenant ID')}
                    >
                      <span className={styles.buttonIcon}>
                        <Icon type="pencil" /> {t('Edit')}
                      </span>
                    </Button>
                    <Button
                      onClick={() => removeTenantIdFromState(tenantIds[0].id)}
                      disabled={disabled || editingTenant.id !== null}
                      variant="error"
                      data-testid="delete-tenant-id-button"
                      aria-label={t('Delete tenant ID')}
                    >
                      <span className={styles.buttonIcon}>
                        <Icon type="trash" /> {t('Delete')}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>

              {editingTenant.id !== null && (
                <div
                  className={styles.editingContainer}
                  data-testid="edit-mode-container"
                >
                  <TextField
                    id={`edit-${editingTenant.id}`}
                    value={editingTenant.value}
                    onChange={value => handleEditChange(value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        saveEditedTenantId();
                      } else if (e.key === 'Escape') {
                        cancelEditing();
                      }
                    }}
                    label={t('Edit Tenant ID')}
                    error={editingTenant.error}
                  />
                  <div
                    className={styles.buttonGroup}
                    style={{ marginTop: 'var(--space-small)' }}
                  >
                    <Button
                      onClick={saveEditedTenantId}
                      data-testid="save-edit-button"
                      aria-label={t('Save edited tenant ID')}
                    >
                      {t('Save')}
                    </Button>
                    <Button
                      onClick={cancelEditing}
                      variant="secondary"
                      data-testid="cancel-edit-button"
                      aria-label={t('Cancel editing tenant ID')}
                    >
                      {t('Cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isSaving && (
            <div className={styles.loaderContainer}>
              <ScrimmedLoader label={t('Saving tenant IDs...')} />
            </div>
          )}
        </form>
      </Form>
    </>
  );
};

export default AxeAssistantTeamsConfiguration;
